// Camera scanning helpers that need no React: the detector contract, the duplicate-read filter
// and the beep-and-vibrate feedback. The component is ScannerView.tsx.
import { playBeep } from './beep';

/** One barcode the detector found. Both the browser's BarcodeDetector and the polyfill return this. */
export interface DetectedCode {
  rawValue: string;
  format?: string | undefined;
}

/** The slice of the Barcode Detection API the scanner uses. */
export interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<readonly DetectedCode[]>;
}

/** Loads (or builds) a detector. May be slow the first time, for example while a polyfill loads. */
export type DetectorLoader = () => Promise<BarcodeDetectorLike>;

/** A box barcode is Code128 (`EB0000012344`). */
export const BOX_BARCODE_FORMATS = ['code_128'] as const;

/** The same code seen again inside this window is one read, not two. */
export const SCAN_DEBOUNCE_MS = 2000;

export interface ScanDebouncer {
  /**
   * True when the code should be reported. A code seen within `windowMs` of the last time it was
   * seen is dropped, and the window slides: a box held in front of the camera is reported once.
   */
  accept(code: string): boolean;
  reset(): void;
}

export function createScanDebouncer(
  windowMs: number = SCAN_DEBOUNCE_MS,
  now: () => number = Date.now,
): ScanDebouncer {
  const lastSeen = new Map<string, number>();
  return {
    accept(code) {
      const t = now();
      const prev = lastSeen.get(code);
      lastSeen.set(code, t);
      if (lastSeen.size > 200) {
        // Forget anything that is long out of the window so a long session stays small.
        for (const [key, seen] of lastSeen) if (t - seen > windowMs) lastSeen.delete(key);
      }
      return prev === undefined || t - prev >= windowMs;
    },
    reset: () => lastSeen.clear(),
  };
}

export type ScanVerdict = 'ok' | 'warn' | 'bad';

/** A short beep and a buzz. Both are silent where the phone or the browser has none. */
export function scanFeedback(verdict: ScanVerdict = 'ok'): void {
  playBeep(verdict === 'bad' ? 'error' : 'ok');
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(verdict === 'ok' ? 60 : [90, 60, 90]);
    }
  } catch (err) {
    console.warn('Vibration unavailable.', err);
  }
}

interface NativeBarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

/**
 * The browser's own BarcodeDetector (Chrome on Android), or null when it is missing or cannot read
 * the formats asked for. Some Android builds have the API but not Code128.
 */
export async function loadNativeDetector(
  formats: readonly string[] = BOX_BARCODE_FORMATS,
): Promise<BarcodeDetectorLike | null> {
  const Native = (globalThis as { BarcodeDetector?: NativeBarcodeDetectorConstructor })
    .BarcodeDetector;
  if (!Native) return null;
  try {
    if (Native.getSupportedFormats) {
      const supported = await Native.getSupportedFormats();
      if (!formats.every((f) => supported.includes(f))) return null;
    }
    return new Native({ formats: [...formats] });
  } catch (err) {
    console.warn('The built-in barcode detector could not start.', err);
    return null;
  }
}
